<template>
  <card-dialog
    v-model="modalVisible"
    :title="$t('event_details.event_player_details.dialog.header')"
    max-width="800px"
  >
    <v-card-text v-if="!playerLoading">
      <div class="rounded-lg grey-bg pa-2 mb-2 mt-4 text-body-1">
        <v-row
          no-gutters
          align="center"
        >
          <v-col
            cols="3"
            class="px-4 font-weight-medium"
          >
            {{ $t('event.label.title') }}
          </v-col>
          <v-col class="py-1">
            {{ $t("add_feedback.dialog.eventDetails", {title: event.title, date: $dayjs(event.start_at).format('dddd, MMMM D'), start: $dayjs(event.start_at).format(getDateTimeFormatForUser('HH:mm')), end: $dayjs(event.end_at).format('HH:mm')}) }}
          </v-col>
        </v-row>
      </div>
      <div class="rounded-lg grey-bg pa-2 mb-4 text-body-1">
        <v-row
          no-gutters
          align="center"
        >
          <v-col
            cols="3"
            class="px-4 font-weight-medium"
          >
            {{ $t("player.label.title") }}
          </v-col>
          <v-col class="py-1">
            {{ player.first_name }} {{ player.last_name }}
          </v-col>
        </v-row>
      </div>

      <v-row
        no-gutters
        align="center"
      >
        <v-col>
          <v-tabs
            v-model="tab"
            background-color="transparent"
            dense
          >
            <v-tab
              v-if="$can('view-player-media')"
              class="text-none text-body-1"
              href="#media"
            >
              {{ $t('event_details.media') }}
            </v-tab>
            <v-tab
              v-if="$can('view-training-protocols') && player.training_exercises && player.training_exercises.length > 0"
              class="text-none text-body-1"
              href="#training"
            >
              {{ $t('event_details.training') }}
            </v-tab>
            <v-tab
              v-if="$can('view-player-notes')"
              class="text-none text-body-1"
              href="#notes"
            >
              {{ $t('individual_analysis.notes') }}
            </v-tab>
            <v-tab
              v-if="!hasItlModule || (hasItlModule && event.editing_rules.feedback_enabled && player.feedback_request && player.feedback_request.submitted_at)"
              class="text-none text-body-1"
              href="#feedback"
            >
              {{ $t('button.feedback') }}
            </v-tab>
          </v-tabs>
        </v-col>
        <v-col
          v-if="$can('delete-performance')"
          cols="auto"
        >
          <v-btn
            v-if="player.has_gps_stats || player.has_hr_stats"
            small
            color="grey"
            class="text-none"
            text
            @click="deleteSettings.sensor_stats = true; performanceDeleteConfirmationDialogVisible = true"
          >
            <v-icon
              x-small
              left
            >
              fal fa-trash
            </v-icon>
            {{ $t("event_details.clear_performance_data") }}
          </v-btn>
        </v-col>
      </v-row>
      <v-divider class="mb-6" />

      <div
        v-if="tab === 'media' && ($can('view-player-media') || $can('create-player-media'))"
      >
        <div
          class="droparea"
          style="min-height: 500px"
          @dragenter.prevent="dragenter"
        >
          <div v-if="$can('create-player-media')">
            <v-overlay
              color="white"
              light
              :value="fileUpload.isDragging"
              :opacity="0.8"
              @dragleave.prevent="dragleave"
              @dragover.prevent="dragover"
              @drop.prevent="drop"
            >
              <div
                class="mb-4 text-center"
                style="pointer-events: none"
              >
                <v-icon
                  x-large
                  color="grey darken-4"
                >
                  fal fa-upload
                </v-icon>
              </div>
              <div
                class="grey--text text--darken-4"
                style="pointer-events: none"
              >
                {{ $t('card_title.release_to_upload') }}
              </div>
            </v-overlay>

            <v-row
              align="center"
              class="mb-4"
            >
              <v-col class="text-body-2 grey--text">
                {{ $t('media_upload.instruction') }}
              </v-col>
              <v-col cols="auto">
                <v-btn
                  color="grey darken-2"
                  text
                  class="text-none"
                  @click="$refs.uploadFile.click()"
                >
                  {{ $t('button.browse') }}
                  <v-icon
                    right
                    small
                  >
                    fal fa-upload
                  </v-icon>
                </v-btn>
              </v-col>
            </v-row>

            <input
              id="upload-file"
              ref="uploadFile"
              type="file"
              multiple
              style="display: none"
              accept="video/*,audio/*,image/*,.doc,.docx,.pptx,.xlsx,.xls,application/pdf,application/octet-stream,.dkg,.csv"
              @input="handleFileChange"
            >
          </div>

          <div v-if="$can('view-player-media')">
            <v-row>
              <v-col
                v-for="(mediaItem, mediaItemIndex) in media"
                :key="mediaItem.id"
                cols="4"
              >
                <media-grid-item
                  :media-item="mediaItem"
                  :can-delete="$can('delete-player-media')"
                  @delete="mediaItem.id ? startDelete(mediaItem) : media.splice(mediaItemIndex, 1)"
                  @preview="preview(mediaItem)"
                />
              </v-col>
            </v-row>
          </div>
        </div>
      </div>
      <div
        v-else-if="tab === 'notes' && ($can('view-player-notes') || $can('create-player-notes'))"
      >
        <div v-if="$can('create-player-notes')">
          <validation-observer ref="noteValidation">
            <validation-provider
              v-slot="{ errors }"
              rules="required"
            >
              <rich-text-editor
                v-model="newNote.text"
                :placeholder="$t('text_input.notes_about_player.placeholder')"
              />
              <div
                v-if="errors.length > 0"
                class="caption error--text"
              >
                {{ errors[0] }}
              </div>
            </validation-provider>

            <v-row
              no-gutters
              align="center"
              class="mt-3"
            >
              <v-col
                cols="auto"
              >
                <v-switch
                  v-model="newNote.isPlayerVisible"
                  :label="$t('switch.visible_to_player') "
                  dense
                  hide-details
                  class="pa-0 ma-0"
                />
              </v-col>
              <v-spacer />
              <v-col cols="auto">
                <v-btn
                  color="primary"
                  :loading="newNote.submitting"
                  @click="storeEventPlayerNote()"
                >
                  {{ $t('training_builder.button.save') }}
                </v-btn>
              </v-col>
            </v-row>
          </validation-observer>
        </div>

        <v-divider
          v-if="player.notes.length > 0 && $can('create-player-notes') && $can('view-player-notes')"
          class="my-4"
        />

        <div v-if="$can('view-player-notes')">
          <player-note
            v-for="note in player.notes"
            :key="note.id"
            :note="note"
            :has-pinned-option="false"
            :has-visible-by-player-option="true"
            :can-edit="$can('edit-player-notes')"
            :can-delete="$can('delete-player-notes')"
            class="mb-3"
            @update="updateNote"
            @delete="deleteNote"
          />
        </div>
      </div>
      <div v-else-if="tab === 'training' && $can('view-training-protocols')">
        <div class="mb-2">
          <v-row
            no-gutters
            class="mb-2 mt-6 grey--text text-body-2"
            align="center"
          >
            <v-col>{{ $t('card_title.protocol_exercises') }}</v-col>
            <v-col cols="auto">
              <v-btn
                text
                small
                class="text-none"
                @click="exercisesPeriodsDialog = true"
              >
                <v-icon
                  left
                  small
                >
                  far fa-clock
                </v-icon>
                {{ $t('button.add_timings') }}
              </v-btn>
              <v-btn
                text
                small
                color="primary"
                class="text-none"
                @click="exercisesDialog = true"
              >
                <v-icon
                  left
                  small
                >
                  far fa-pen
                </v-icon>
                {{ $t('button.adjust_protocol') }}
              </v-btn>
            </v-col>
          </v-row>

          <div
            v-for="exercise in player.training_exercises"
            :key="exercise.id"
            class="mb-1"
          >
            <superset-selected-card
              v-if="exercise.type === 'superset'"
              :orderable="false"
              :editable="false"
              show-media
              :item.sync="exercise"
              feedback-allowed
              @feedback="updatePlayerTrainingExerciseRealization"
            />
            <group-selected-card
              v-else-if="exercise.type === 'group'"
              :item.sync="exercise"
              show-media
              :editable="false"
              :orderable="false"
              feedback-allowed
              @feedback="updatePlayerTrainingExerciseRealization"
            />
            <pause-selected-card
              v-else-if="exercise.type === 'pause'"
              :item.sync="exercise"
              show-media
              :editable="false"
              :orderable="false"
            />
            <exercise-selected-card-template
              v-else-if="exercise.group"
              :orderable="false"
              :editable="false"
              show-media
              :exercise="exercise"
              feedback-allowed
              @feedback="updatePlayerTrainingExerciseRealization"
            />
          </div>

          <training-protocol-form-dialog
            v-if="player.training_exercises"
            :visible.sync="exercisesDialog"
            :edit-items="player.training_exercises"
            :listen-events="false"
            :player="player"
            history-allowed
            :edit-title="player.training_protocol_title || (event.training_protocol && event.training_protocol.name)"
            :edit-description="player.training_protocol_description || (event.training_protocol && event.training_protocol.description)"
            edit-title-allowed
            edit-description-allowed
            @update:edit-items="updatePlayerTrainingExercises"
          />

          <exercises-periods-dialog
            :visible.sync="exercisesPeriodsDialog"
            :training-exercises="player.training_exercises"
            :player="player"
            :event="event"
            @update:edit-items="updatePlayerTrainingExercises"
          />
        </div>
      </div>
      <div v-else-if="tab === 'feedback'">
        <div v-if="!player.has_attended && player.event_missed_reason">
          <v-alert
            v-if="hasItlModule"
            type="error"
            outlined
            class="text-body-1"
          >
            <p>{{ $t('event_details.player_did_not_attend_due_to') }} <strong>{{ $t('not_attended_reason.' + toCamelCase(player.event_missed_reason.reason)) }}</strong></p>
            <p class="mb-0">
              {{ $t('event_details.submit_feedback_to_clear') }}
            </p>
          </v-alert>
        </div>
        <validation-observer ref="feedbackInputValidation">
          <feedback-input
            :feedback-form.sync="feedbackForm"
            :player="player"
            :disabled="hasItlModule && feedbackDisabled"
            :event-missed-reasons="eventMissedReasons.data"
          />
        </validation-observer>
      </div>

      <media-viewer-dialog
        :visible.sync="mediaViewerDialog.visible"
        :media="media"
        :view-media-id="mediaViewerDialog.media ? mediaViewerDialog.media.id : undefined"
        :player="player"
        :view-only="!$can('edit-player-media')"
        :can-delete="$can('delete-player-media')"
        @media:updated="updateMedia"
        @media:deleted="removeMedia"
      />

      <delete-confirmation-dialog
        :visible.sync="deleteConfirmationDialogVisible"
        :submitting="deleting"
        @confirmed="destroy()"
      />

      <delete-confirmation-dialog
        :visible.sync="performanceDeleteConfirmationDialogVisible"
        :submitting="deletingPerformance"
        :text="deleteSettings.sensor_stats ? $t('delete_confirm.clear_player_sensor_data') : $t('delete_confirm.clear_player_feedback_data')"
        :cta-text="$t('delete_confirm.yes_clear')"
        @confirmed="destroyPlayerStats()"
      />
    </v-card-text>

    <v-card-text
      v-else
      class="py-16"
    >
      <v-row
        no-gutters
        justify="center"
      >
        <v-col cols="auto">
          <v-progress-circular
            :size="80"
            :width="2"
            indeterminate
          />
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-actions>
      <v-btn
        v-if="hasItlModule && tab === 'feedback'"
        text
        class="text-none"
        color="grey"
        :disabled="feedbackDisabled"
        @click="deleteSettings.feedback_stats = true; performanceDeleteConfirmationDialogVisible = true"
      >
        {{ $t('button.clear_feedback') }}
      </v-btn>
      <v-btn
        v-if="hasItlModule && tab === 'feedback' && player.feedback_request && player.feedback_request.submitted_at"
        color="error"
        text
        class="text-none"
        :disabled="feedbackDisabled"
        @click="eventMissedDialog = true"
      >
        {{ $t('card_title.did_not_attend') }}
      </v-btn>
      <v-spacer />
      <v-btn
        text
        color="grey"
        @click="modalVisible = false"
      >
        {{ $t('button.cancel') }}
      </v-btn>
      <v-btn
        v-if="tab === 'feedback'"
        color="primary"
        :loading="feedbackSaving"
        :disabled="feedbackDisabled"
        @click="submitFeedback()"
      >
        {{ $t('button.submit') }}
      </v-btn>
    </v-card-actions>

    <card-dialog
      v-model="eventMissedDialog"
      :title="$t('select_reason.dialog.header')"
      max-width="300px"
      :persistent="false"
    >
      <v-card-text class="pa-0">
        <v-list dense>
          <v-list-item-group
            v-model="eventMissedReasonId"
            color="primary"
          >
            <v-list-item
              v-for="reason in eventMissedReasons.data"
              :key="reason.id"
              :value="reason.id"
              :input-value="reason.id"
            >
              <v-list-item-content>
                <v-list-item-title class="text-body-1">
                  {{ reason.reason }}
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-action v-if="eventMissedReasonId === reason.id">
                <v-icon small>
                  far fa-check
                </v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn
          color="primary"
          :disabled="!eventMissedReasonId"
          :loading="submittingMissed"
          @click="submitMissed()"
        >
          {{ $t('button.submit') }}
        </v-btn>
      </v-card-actions>
    </card-dialog>
  </card-dialog>
</template>

<script>
import CardDialog from '@/components/helpers/CardDialog.vue'
import DeleteConfirmationDialog from '@/components/dialogs/DeleteConfirmationDialog.vue'
import { cloneDeep } from 'lodash'
import _axios from '@/plugins/axios'
import { MediaApiService } from '@/data/services/MediaApiService'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import MediaViewerDialog from '@/components/dialogs/MediaViewerDialog.vue'
import RichTextEditor from '@/components/inputs/RichTextEditor.vue'
import { EventApiService } from '@/data/services'
import MediaGridItem from '@/components/helpers/MediaGridItem.vue'
import SupersetSelectedCard from '@/components/training-builder/SupersetSelectedCard.vue'
import GroupSelectedCard from '@/components/training-builder/GroupSelectedCard.vue'
import PauseSelectedCard from '@/components/training-builder/PauseSelectedCard.vue'
import TrainingProtocolFormDialog from '@/components/dialogs/TrainingProtocolFormDialog.vue'
import ExerciseSelectedCardTemplate from '@/components/training-builder/ExerciseSelectedCardTemplate.vue'
import ExercisesPeriodsDialog from '@/components/training-builder/ExercisesPeriodsDialog.vue'
import PlayerNote from '@/components/players/PlayerNote.vue'
import FeedbackInput from '@/components/feedback/FeedbackInput.vue'
import { getDateTimeFormatForUser, toCamelCase } from '@/utils/helpers'
import { musclesAndJoints } from '@/data/models'

export default {
  name: 'EventPlayerDialog',
  components: {
    PlayerNote,
    FeedbackInput,
    ExercisesPeriodsDialog,
    ExerciseSelectedCardTemplate,
    TrainingProtocolFormDialog,
    PauseSelectedCard,
    GroupSelectedCard,
    SupersetSelectedCard,
    MediaGridItem,
    RichTextEditor,
    MediaViewerDialog,
    DeleteConfirmationDialog,
    CardDialog
  },
  mixins: [deleteConfirmationMixin],
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    event: {
      type: Object,
      required: true
    },
    playerId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      tab: 'media',
      playerLoading: false,
      player: {},
      fileUpload: {
        isDragging: false
      },
      media: [],
      mediaViewerDialog: {
        visible: false,
        media: undefined
      },
      feedbackForm: {},
      deleteMedia: undefined,
      deleting: false,
      newNote: {
        text: '',
        isPinned: false,
        isPlayerVisible: false,
        submitting: false
      },
      feedbackSaving: false,
      exercisesDialog: false,
      performanceDeleteConfirmationDialogVisible: false,
      deleteSettings: {
        sensor_stats: false,
        feedback_stats: false
      },
      deletingPerformance: false,
      exercisesPeriodsDialog: false,
      eventMissedDialog: false,
      eventMissedReasonId: null,
      eventMissedReasons: {
        loading: false,
        data: []
      },
      submittingMissed: false
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    hasItlModule() {
      return this.user.organisation_settings.has_itl_module || false
    },
    feedbackDisabled() {
      return this.$dayjs(this.event.end_at).isBefore(this.$dayjs().subtract(3, 'day'))
    }
  },
  watch: {
    visible(newValue) {
      if (newValue) {
        this.init()
        this.fetchPlayerDetails()
      }
    },
    performanceDeleteConfirmationDialogVisible(newValue) {
      if (!newValue) {
        this.deleteSettings.sensor_stats = false
        this.deleteSettings.feedback_stats = false
      }
    }
  },
  created() {
    this.fetchPlayerDetails()

    const this2 = this

    this.keyListener = (event) => {
      if (event.key === 'ArrowRight') {
        this2.nextMedia()
      } else if (event.key === 'ArrowLeft') {
        this2.previousMedia()
      }
    }

    window.addEventListener('keyup', this.keyListener)

    this.fetchEventMissedReasons()
  },
  beforeDestroy() {
    window.removeEventListener('keyup', this.keyListener)
  },
  methods: {
    getDateTimeFormatForUser,
    toCamelCase,
    async fetchPlayerDetails() {
      this.playerLoading = true

      this.player = {}

      try {
        const result = await EventApiService.getEventPlayerDetails(this.event.id, this.playerId)

        this.player = result.data

        this.init()
      } catch (e) {

      } finally {
        this.playerLoading = false
      }
    },
    async init() {
      if (this.tab === 'training' && (!this.player.training_exercises || this.player.training_exercises.length === 0)) {
        this.tab = 'media'
      }

      this.media = cloneDeep(this.player.media)

      this.newNote.text = ''
      this.newNote.isPlayerVisible = false

      const defaultPainObject = {}

      Object.keys(musclesAndJoints).forEach(key => {
        defaultPainObject[key.toUpperCase()] = 0
      })

      this.feedbackForm = {
        pain: this.player.feedback_request?.submitted_data?.pain || defaultPainObject,
        trainingGrade: this.player.feedback_request?.submitted_data?.training_grade || 5.5,
        sessionEnjyoment: this.player.feedback_request?.submitted_data?.session_enjoyment || 3,
        hasAttended: this.player.has_attended,
        eventMissedReasonId: this.player.event_missed_reason ? this.player.event_missed_reason.id : null
      }

      if (this.feedbackForm.eventMissedReasonId) {
        this.eventMissedReasonId = this.feedbackForm.eventMissedReasonId
      } else {
        this.eventMissedReasonId = null
      }
    },
    async handleFileChange() {
      await this.$nextTick()

      const files = [...this.$refs.uploadFile.files]

      const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'dkg', 'csv']

      for (const file of files) {
        const type = file.type
        const extension = file.name.split('.').pop()

        if (type.match('image.*') || type.match('video.*') || type.match('audio.*') || allowedExtensions.indexOf(extension) > -1) {
          this.media.unshift({
            file: file,
            name: file.name,
            progress: 0,
            mime_type: file.type,
            size: file.size,
            upload: true
          })
        } else {
          this.media.unshift({
            file: file,
            name: file.name,
            progress: 0,
            mime_type: file.type,
            size: file.size,
            error: 'File type not allowed'
          })
        }
      }

      this.$refs.uploadFile.value = null

      this.processUpload()
    },
    async processUpload() {
      let formData

      for (const file of this.media.filter(m => !!m.upload)) {
        formData = new FormData()
        formData.append('file', file.file, file.name)

        _axios.post(`events/${this.event.id}/media-upload/${this.player.id}`, formData, {
          onUploadProgress: (e) => {
            file.progress = Number((e.loaded / e.total) * 100)
          }
        }).then(response => {
          file.upload = false

          Object.assign(file, response.data.media)

          this.$emit('success')
        }).catch(error => {
          if (error.response && error.response.status === 422) {
            this.$store.dispatch(this.$storeActions.ALERT, {
              color: 'error',
              text: this.$t('alert.file_type_not_allowed.error')
            })

            file.error = this.$t('alert.file_type_not_allowed.error')

            return
          }

          this.$store.dispatch(this.$storeActions.ALERT, {
            color: 'error',
            text: this.$t('alert.uploading_file.error')
          })

          file.error = this.$t('event_details.media_upload.error')
        })
      }
    },
    preview(media) {
      this.mediaViewerDialog.visible = true
      this.mediaViewerDialog.media = media
    },
    dragover(e) {
      this.fileUpload.isDragging = true
    },
    dragleave(e) {
      this.fileUpload.isDragging = false
    },
    dragenter(e) {
      this.fileUpload.isDragging = true
    },
    drop(e) {
      this.$refs.uploadFile.files = e.dataTransfer.files
      this.handleFileChange()
      this.fileUpload.isDragging = false
    },
    startDelete(media) {
      this.deleteMedia = media
      this.deleteConfirmationDialogVisible = true
    },
    async destroy() {
      if (!this.deleteMedia) return

      const id = this.deleteMedia.id

      try {
        this.deleting = true

        await MediaApiService.delete(id)

        const index = this.media.findIndex(m => m.id === id)

        if (index > -1) this.media.splice(index, 1)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.media_delete.success'),
          color: 'success'
        })

        this.deleteConfirmationDialogVisible = false

        this.deleteMedia = undefined

        this.$emit('success')
        // eslint-disable-next-line no-useless-catch
      } catch (e) {
        throw e
      } finally {
        this.deleting = false
      }
    },
    async storeEventPlayerNote() {
      if (!(await this.$refs.noteValidation.validate())) return

      this.newNote.submitting = true

      try {
        const result = await EventApiService.storeEventPlayerNote(this.event.id, this.player.id, this.newNote.text, this.newNote.isPlayerVisible)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.player_note.save.success'),
          color: 'success'
        })

        this.newNote.text = ''
        this.newNote.isPlayerVisible = false

        setTimeout(() => {
          this.$refs.noteValidation.reset()
        }, 10)

        this.player.notes.unshift(result.note)

        this.$emit('success')
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.player_note.save.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.newNote.submitting = false
      }
    },
    async submitFeedback() {
      if (!(await this.$refs.feedbackInputValidation.validate())) return

      this.feedbackSaving = true

      try {
        let payload

        if (this.hasItlModule) {
          payload = {
            training_grade: this.feedbackForm.trainingGrade,
            pain: this.feedbackForm.pain,
            session_enjoyment: this.feedbackForm.sessionEnjyoment
          }
        } else {
          payload = {
            has_attended: this.feedbackForm.hasAttended,
            event_missed_reason_id: this.feedbackForm.eventMissedReasonId
          }
        }

        await EventApiService.storeEventPlayerFeedback(this.event.id, this.player.id, payload)

        this.$emit('success')

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.feedback_submit.success'),
          color: 'success'
        })

        this.modalVisible = false
      } catch (e) {
        if (e.response && e.response.data.error) {
          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('alert.not_allowed.error'),
            color: 'error'
          })

          return
        }

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.feedback_submit.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.feedbackSaving = false
      }
    },
    updateMedia(media) {
      const index = this.media.findIndex(m => m.id === media.id)

      if (index > -1) {
        this.media.splice(index, 1, media)
      }

      this.$emit('success')
    },
    removeMedia(media) {
      const index = this.media.findIndex(m => m.id === media.id)

      if (index > -1) {
        this.media.splice(index, 1)
      }

      this.$emit('success')
    },
    async updatePlayerTrainingExercises(exercises, protocolDetails = { name: undefined, description: undefined }) {
      try {
        await EventApiService.storeEventPlayerTrainingExercises(this.event.id, this.player.id, exercises, protocolDetails.name, protocolDetails.description)

        await this.fetchPlayerDetails()

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.training_exercise_update.success'),
          color: 'success'
        })
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.training_exercise_update.error'),
          color: 'error'
        })

        throw e
      }
    },
    async updatePlayerTrainingExerciseRealization(realizationParams) {
      try {
        await EventApiService.storeEventPlayerTrainingExercises(this.event.id, this.player.id, this.player.training_exercises)

        this.$emit('success')

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.training_exercise_realization_add.success'),
          color: 'success'
        })
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.training_exercise_update.error'),
          color: 'error'
        })

        throw e
      }
    },
    async destroyPlayerStats() {
      this.deletingPerformance = true

      try {
        await EventApiService.deletePlayerData(this.event.id, this.player.id, {
          sensor_stats: this.deleteSettings.sensor_stats,
          feedback_stats: this.deleteSettings.feedback_stats
        })

        this.$emit('success')

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.destroy_gps.success'),
          color: 'success'
        })

        this.performanceDeleteConfirmationDialogVisible = false

        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.destroy_gps.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.deletingPerformance = false
      }
    },
    updateNote(note) {
      const index = this.player.notes.findIndex(n => n.id === note.id)

      if (index > -1) {
        this.player.notes.splice(index, 1, note)
      }
    },
    deleteNote(note) {
      const index = this.player.notes.findIndex(n => n.id === note.id)

      if (index > -1) {
        this.player.notes.splice(index, 1)
      }

      this.$emit('success')
    },
    async fetchEventMissedReasons() {
      this.eventMissedReasons.loading = true

      try {
        const result = await EventApiService.getEventMissedReasons()
        this.eventMissedReasons.data = result.data.map((reason) => {
          return {
            ...reason,
            reason: this.$t(`not_attended_reason.${toCamelCase(reason.reason)}`)
          }
        })
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_reasons_for_missing_events'),
          color: 'error'
        })

        throw e
      } finally {
        this.eventMissedReasons.loading = false
      }
    },
    async submitMissed() {
      if (!this.eventMissedReasonId) return

      this.submittingMissed = true

      try {
        const submittedFeedback = this.player?.feedback_request?.submitted_data

        if (submittedFeedback && typeof submittedFeedback === 'object' && Object.keys(submittedFeedback).length > 0) {
          await EventApiService.deletePlayerData(this.event.id, this.player.id, {
            feedback_stats: true
          })
        }

        await EventApiService.storeEventPlayerFeedback(this.event.id, this.player.id, {
          event_missed_reason_id: this.eventMissedReasonId
        })

        this.$emit('success')

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.not_attended_submit.success'),
          color: 'success'
        })

        this.eventMissedDialog = false
        this.modalVisible = false
      } catch (e) {
        if (e.response && e.response.data.error) {
          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('alert.not_allowed.error'),
            color: 'error'
          })

          return
        }

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.feedback_submit.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.submittingMissed = false
      }
    }
  }

}
</script>

<style scoped>

</style>
